import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  calculatePortfolioPerformance,
  calculateSharpeRatio,
  calculateMaxDrawdown,
  getSPYPerformance,
  START_DATE
} from '../services/marketDataService';

const Card = styled.div`
  background: #1a1a1a;
  border: 2px solid #ff6b00;
  border-radius: 8px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 4px 20px rgba(255, 107, 0, 0.2);
`;

const CardTitle = styled.h2`
  color: #ff6b00;
  font-size: 1.8rem;
  margin: 0 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 2px;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const DateBadge = styled.div`
  background: #ff6b00;
  color: #000;
  padding: 6px 15px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 700;
`;

const QuickStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin: 25px 0;
`;

const StatBox = styled.div`
  background: #0a0a0a;
  padding: 15px;
  border-radius: 6px;
  border-left: 4px solid ${props => props.color || '#ff6b00'};
`;

const StatLabel = styled.div`
  font-size: 0.85rem;
  color: #888;
  margin-bottom: 5px;
`;

const StatValue = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${props => props.color || '#fff'};
`;

const StatChange = styled.span`
  font-size: 0.9rem;
  color: ${props => props.positive ? '#00ff00' : '#ff0000'};
  margin-left: 8px;
`;

const Section = styled.div`
  margin-bottom: 25px;
  padding-bottom: 20px;
  border-bottom: 1px solid #333;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #ff6b00;
  font-size: 1.2rem;
  margin: 0 0 15px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const EventsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EventItem = styled.div`
  background: #0a0a0a;
  padding: 15px 20px;
  border-radius: 4px;
  border-left: 4px solid ${props =>
    props.impact === 'high' ? '#ff0000' :
    props.impact === 'medium' ? '#ffaa00' : '#00aaff'
  };
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 15px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #1a1a1a;
    transform: translateX(5px);
  }
`;

const EventContent = styled.div`
  flex: 1;
`;

const EventTitle = styled.div`
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 5px;
`;

const EventDescription = styled.div`
  color: #aaa;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const ImpactBadge = styled.div`
  background: ${props =>
    props.impact === 'high' ? 'rgba(255, 0, 0, 0.2)' :
    props.impact === 'medium' ? 'rgba(255, 170, 0, 0.2)' : 'rgba(0, 170, 255, 0.2)'
  };
  color: ${props =>
    props.impact === 'high' ? '#ff0000' :
    props.impact === 'medium' ? '#ffaa00' : '#00aaff'
  };
  padding: 4px 12px;
  border-radius: 3px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
`;

const RecommendationBox = styled.div`
  background: linear-gradient(135deg, rgba(255, 107, 0, 0.1) 0%, rgba(255, 107, 0, 0.05) 100%);
  padding: 20px;
  border-radius: 6px;
  border: 1px solid #ff6b00;
`;

const RecommendationTitle = styled.div`
  color: #ff6b00;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 12px;
  text-transform: uppercase;
`;

const RecommendationText = styled.div`
  color: #ddd;
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 8px;
`;

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const DailyExecutiveSummary = () => {
  const today = new Date();
  const dateString = today.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([
    { label: 'Portfolio YTD', value: '0%', change: '0%', positive: true, color: '#00ff00' },
    { label: 'vs SPY', value: '0%', change: 'Alpha', positive: true, color: '#ff6b00' },
    { label: 'Sharpe Ratio', value: '0', change: 'Loading', positive: true, color: '#00aaff' },
    { label: 'Max Drawdown', value: '0%', change: 'Loading', positive: true, color: '#ffaa00' }
  ]);

  useEffect(() => {
    const fetchPerformanceStats = async () => {
      try {
        setLoading(true);

        // Fetch portfolio performance time series
        const portfolioTimeSeries = await calculatePortfolioPerformance(START_DATE);

        if (portfolioTimeSeries.length === 0) {
          setLoading(false);
          return;
        }

        // Calculate daily returns for Sharpe ratio
        const dailyReturns = [];
        for (let i = 1; i < portfolioTimeSeries.length; i++) {
          const dailyReturn = (portfolioTimeSeries[i].value - portfolioTimeSeries[i - 1].value) / portfolioTimeSeries[i - 1].value;
          dailyReturns.push(dailyReturn);
        }

        // Calculate metrics
        const sharpe = calculateSharpeRatio(dailyReturns);
        const maxDD = calculateMaxDrawdown(portfolioTimeSeries);
        const spyPerf = await getSPYPerformance(START_DATE);
        const latestReturn = portfolioTimeSeries[portfolioTimeSeries.length - 1].return;
        const expectedReturn = 30.0;
        const delta = latestReturn - expectedReturn;
        const alpha = latestReturn - spyPerf;

        // Update stats
        setStats([
          {
            label: 'Portfolio YTD',
            value: `${latestReturn >= 0 ? '+' : ''}${latestReturn.toFixed(1)}%`,
            change: `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}%`,
            positive: delta >= 0,
            color: '#00ff00'
          },
          {
            label: 'vs SPY',
            value: `${alpha >= 0 ? '+' : ''}${alpha.toFixed(1)}%`,
            change: 'Alpha',
            positive: alpha >= 0,
            color: '#ff6b00'
          },
          {
            label: 'Sharpe Ratio',
            value: sharpe.toFixed(2),
            change: sharpe >= 1.5 ? 'Excellent' : sharpe >= 1.0 ? 'Good' : 'Fair',
            positive: true,
            color: '#00aaff'
          },
          {
            label: 'Max Drawdown',
            value: `${maxDD.toFixed(1)}%`,
            change: maxDD > -10 ? 'Controlled' : 'High',
            positive: maxDD > -10,
            color: '#ffaa00'
          }
        ]);

        setLoading(false);
      } catch (error) {
        console.error('Error fetching performance stats:', error);
        setLoading(false);
      }
    };

    const fetchNews = async () => {
      try {
        // Fetch from aggregated sources
        const timestamp = Date.now();
        const response = await fetch(`${API_BASE_URL}/api/news-aggregated?limit=10&_t=${timestamp}`);

        if (response.ok) {
          const newsData = await response.json();

          // KEY EVENTS FILTER: Last 24 hours, high/medium impact, exclude HN spam
          const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
          const yesterdayNews = newsData.filter(item => {
            const itemTime = new Date(item.timestamp).getTime();
            const isRecent = itemTime > oneDayAgo;
            const isImportant = item.impact === 'high' || item.impact === 'medium';
            const notLowQuality = item.source !== 'Hacker News' || item.hnScore > 200;
            return isRecent && isImportant && notLowQuality;
          });

          console.log(`[DailyExecSummary] Filtered to ${yesterdayNews.length} key events from last 24h`);

          const events = yesterdayNews.slice(0, 5).map(item => ({
            impact: item.impact,
            title: item.title,
            description: item.summary.substring(0, 180) + '...',
            url: item.url
          }));
          setKeyEvents(events);
        }
      } catch (error) {
        console.error('Error fetching news:', error);
      }
    };

    fetchPerformanceStats();
    fetchNews();

    // Refresh every 15 minutes
    const interval = setInterval(() => {
      fetchPerformanceStats();
      fetchNews();
    }, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const [keyEvents, setKeyEvents] = useState([]);
  const [marketOverview, setMarketOverview] = useState({
    thesisStatus: 'Loading...',
    performanceSummary: 'Loading...'
  });

  // Generate dynamic market overview based on daily data
  useEffect(() => {
    const generateMarketOverview = () => {
      // Parse portfolio metrics
      const portfolioReturn = parseFloat(stats[0].value);
      const alpha = parseFloat(stats[1].value);
      const sharpe = parseFloat(stats[2].value);
      const maxDD = parseFloat(stats[3].value);

      // Determine thesis status
      const isOnTrack = portfolioReturn >= 20 && alpha >= 0 && sharpe >= 1.0;
      const statusText = isOnTrack ? 'ON TRACK' : 'NEEDS ATTENTION';

      // Build thesis validation text
      const dayOfWeek = today.toLocaleDateString('en-US', { weekday: 'long' });
      const thesisPillars = [];

      // Check each pillar dynamically
      if (portfolioReturn >= 20) {
        thesisPillars.push('(1) Portfolio performance exceeding baseline expectations');
      } else {
        thesisPillars.push('(1) Portfolio performance below +30% target - monitoring closely');
      }

      if (alpha >= 5) {
        thesisPillars.push('(2) Strong alpha vs SPY confirms semiconductor thesis');
      } else if (alpha >= 0) {
        thesisPillars.push('(2) Positive alpha vs SPY but below optimal levels');
      } else {
        thesisPillars.push('(2) Underperforming SPY - reassess sector positioning');
      }

      if (sharpe >= 1.5) {
        thesisPillars.push('(3) Risk-adjusted returns excellent (Sharpe ≥1.5)');
      } else if (sharpe >= 1.0) {
        thesisPillars.push('(3) Risk-adjusted returns acceptable (Sharpe ≥1.0)');
      } else {
        thesisPillars.push('(3) Risk-adjusted returns suboptimal - volatility concerns');
      }

      if (maxDD > -15) {
        thesisPillars.push('(4) Drawdown controlled within acceptable limits');
      } else {
        thesisPillars.push('(4) Elevated drawdown - increased position monitoring required');
      }

      const thesisStatusText = `${dayOfWeek} Assessment - Thesis Status: ${statusText}. ${thesisPillars.join(', ')}.`;

      // Build performance summary
      const performanceText = `YTD return of ${stats[0].value} (${stats[0].change} vs expected +30%), outperforming SPY by ${stats[1].value}. Risk metrics: Sharpe ${stats[2].value} (${stats[2].change.toLowerCase()}), max drawdown ${stats[3].value} (${stats[3].change.toLowerCase()}). ${keyEvents.length} high-impact events identified in last 24 hours.`;

      setMarketOverview({
        thesisStatus: thesisStatusText,
        performanceSummary: performanceText
      });
    };

    if (stats[0].value !== '0%' && !loading) {
      generateMarketOverview();
    }
  }, [stats, keyEvents, loading, today]);

  return (
    <Card>
      <CardTitle>
        📋 Daily Executive Summary
        <DateBadge>{dateString}</DateBadge>
      </CardTitle>

      <QuickStats>
        {stats.map((stat, idx) => (
          <StatBox key={idx} color={stat.color}>
            <StatLabel>{stat.label}</StatLabel>
            <StatValue color={stat.color}>
              {stat.value}
              {stat.change && (
                <StatChange positive={stat.positive}> {stat.change}</StatChange>
              )}
            </StatValue>
          </StatBox>
        ))}
      </QuickStats>

      <Section>
        <SectionTitle>Market Overview</SectionTitle>
        <RecommendationText>
          <strong>{marketOverview.thesisStatus}</strong>
        </RecommendationText>
        <RecommendationText>
          <strong>Portfolio Performance:</strong> {marketOverview.performanceSummary}
        </RecommendationText>
      </Section>

      <Section>
        <SectionTitle>Key Events (Last 24 Hours)</SectionTitle>
        <EventsList>
          {keyEvents.map((event, idx) => (
            <EventItem
              key={idx}
              impact={event.impact}
              onClick={() => window.open(event.url, '_blank', 'noopener,noreferrer')}
            >
              <EventContent>
                <EventTitle>{event.title}</EventTitle>
                <EventDescription>{event.description}</EventDescription>
              </EventContent>
              <ImpactBadge impact={event.impact}>{event.impact}</ImpactBadge>
            </EventItem>
          ))}
        </EventsList>
      </Section>
    </Card>
  );
};

export default DailyExecutiveSummary;
